import {
  Flight,
  FlightSearchParams,
  AirportInfo,
  FlightBaggageInfo,
  FlightLayoverInfo,
  FlightUserPreferences,
  FlightFaresPolicy,
  FlightFarePrice,
  FlightFare,
  FlightStopsTypeMultifare,
  FlightCabinType,
  FlightTag,
  FlightSegment,
} from './flight';
import { UserContact, UserTraveler } from './user';
import { Hotel, HotelRoom, HotelRoomCancellationInfo } from './hotel';

import { TravelerGender, TravelerTitle, TravelerType } from './traveler';

export enum BookStatuses {
  BOOK_COMPLETED = 'completed',
  BOOK_CONFIRMED = 'confirmed',
  BOOK_NEEDPAYMENT = 'needpayment',
  BOOK_PROCESSING = 'processing',
  BOOK_PAYSUCCESS = 'paymentsuccess',
  BOOK_PAYCONFIRM = 'paymentconfirm',
  BOOK_PAYMENT_IN_PROCESS = 'paymentinprocess',
  BOOK_DONE = 'bookdone',
  BOOK_PAYMENT_DONE = 'done',
  BOOK_ISSUED = 'issued',
  BOOK_EXPIRED = 'expired',
  BOOK_ERROR = 'bookerror',
  BOOK_FAILED = 'bookfailed',
  BOOK_WAITING = 'waiting',
  BOOK_CANCELLED = 'cancelled',
  BOOK_PRICE_CHANGED = 'pricechanged',
  BOOK_WAITING_CHANGED = 'waitingchanged',
  BOOK_NONE = 'none',
  REQUEST_PROCESS_AFS = 'request',
}

export type BookContact = Omit<UserContact, 'id' | 'status' | 'lang'> & { gender?: string };

export enum DocumentType {
  ID_CARD = 'idCard',
  PASSPORT = 'passport',
}

export enum AfterSalesType {
  CHANGE_PASSENGER_DATA = 'changedata',
  ADD_BAGGAGE = 'addbaggage',
  CHANGE_FLIGHT = 'changeflight',
  REFUND = 'refund',
}

export enum FlightBookSelectionType {
  DEPART = 'depFlight',
  RETURN = 'retFlight',
}

export type FlightSubmitParams = {
  country: string;
  search: FlightSearchParams;
  depFlight: Flight;
  retFlight: Flight;
  lang: string;
  ads?: object;
  userPreferences?: FlightUserPreferences;
};

export type FlightSubmitResponse = {
  bookId: string;
};

export type FlightBookVerifyResponse = {
  progress: number;
  total: number;
  flights: {
    [FlightBookSelectionType.DEPART]: Flight;
    retFlight?: Flight;
  };
};

export type FlightBookFreeBaggageInfo = {
  size: number;
  isAdditional: boolean;
};

export type FlightBookFormRules = {
  nameSpaceSupport: boolean;
  unicodeSupport: boolean;
  idCardPassportOptional: boolean;
  ageLimit: {
    adult: {
      min: number;
      max: number;
      minWithoutCompanion: number;
    };
    child: {
      min: number;
      max: number;
    };
    infant: {
      min: number;
      max: number;
    };
  };
  passenger: {
    firstName: {
      max: number;
      min: number;
      maxUnicode: number;
      minUnicode: number;
    };
    idCard: {
      [key: string]: {
        len: number;
        numericOnly: boolean;
      };
    };
    lastName: {
      max: number;
      min: number;
      maxUnicode: number;
      minUnicode: number;
    };
    passport: {
      minDays: number;
    };
  };
  contact: {
    firstName: {
      max: number;
      min: number;
      maxUnicode: number;
      minUnicode: number;
    };
    lastName: {
      max: number;
      min: number;
      maxUnicode: number;
      minUnicode: number;
    };
  };
};

export type FlightBookContact = BookContact;

export type FlightBookPassenger = {
  type: TravelerType;
  title: TravelerTitle;
  gender: TravelerGender;
  firstName: string;
  lastName: string;
  dob?: string;
  nationality?: string;
  idCard?: string;
  idCardCountry?: string;
  idCardExpDate?: string;
  passport?: string;
  passportCountry?: string;
  passportExpDate?: string;
};

export type FlightBookBaggage = Partial<Record<FlightBookSelectionType, Array<FlightBaggageInfo | null>>>;

export type FlightBookFreeBaggage = Partial<
  Record<FlightBookSelectionType, Array<(FlightBaggageInfo & { isAdditional: boolean }) | null>>
>;

export type FlightBookPassengerWithAddOn = FlightBookPassenger & {
  baggage?: FlightBookBaggage;
};

export type FlightBookPassengerConfig = FlightBookPassengerWithAddOn & {
  position: number;
  documentType?: DocumentType;
};

export type FlightBookPassengerFormConfig = {
  firstname: boolean;
  dob: boolean;
  idCard: boolean;
  idCardCountry: boolean;
  idCardExpDate: boolean;
  nationality: boolean;
  passport: boolean;
  passportCountry: boolean;
  passportExpDate: boolean;
};

export type FlightBookFormConfig = {
  expired: string;
  form: Record<TravelerType, FlightBookPassengerFormConfig> & {
    useGenderInput: boolean;
    newsletter: boolean;
    hasAlphaNameNotification: boolean;
    prioritizeLastName: boolean;
  };
  rules: FlightBookFormRules;
  baggageOptions: {
    [FlightBookSelectionType.DEPART]: Array<Array<FlightBaggageInfo>>;
    [FlightBookSelectionType.RETURN]?: Array<Array<FlightBaggageInfo>>;
  };
  fba: {
    [FlightBookSelectionType.DEPART]: Array<FlightBookFreeBaggageInfo>;
    [FlightBookSelectionType.RETURN]?: Array<FlightBookFreeBaggageInfo>;
  };
  popularCountryOption: string[];
  passengerOptions: UserTraveler[];
  customerOptions: UserContact[];
  selectedFlight: {
    [FlightBookSelectionType.DEPART]: Flight;
    [FlightBookSelectionType.RETURN]?: Flight;
  };
  search: FlightSearchParams & { currency: string };
  gfsCurrency?: string;
  gfsTotal?: number;
};

export type BookStatus = {
  expiryDate?: string;
  bookStatus: string;
  paymentStatus: string;
  orderStatus: Array<{
    orderId: string;
    status: string;
  }>;
};

export type FlightBookOrderFareInfo = {
  price: number;
  tax: number;
  fuel: number;
  insurance: number;
  service: number;
  other: number;
};
export type FlightBookOrderFaresSummary = {
  price: number;
  tax: number;
  fuel: number;
  insurance: number;
  service: number;
  other: number;
  baggage: number;
  disc: number;
};

export type FlightBookOrderFaresDetail = {
  adult: FlightBookOrderFareInfo;
  child?: FlightBookOrderFareInfo;
  infant?: FlightBookOrderFareInfo;
};

export type FlightBookOrderDetail = {
  type: string;
  airlineName: string;
  orderId: number;
  orderStatus: string;
  code: string;
  traCode: string;
  depAirport: string;
  arrAirport: string;
  traAirport: string;
  depAirportInfo: AirportInfo;
  arrAirportInfo: AirportInfo;
  traAirportInfo: AirportInfo | null;
  depDateTime: string;
  arrDateTime: string;
  traArrDateTime: string;
  traDepDateTime: string;
  issuedTime: number | null;
  traIssuedTime: number | null;
  pnr: string;
  pnrExpDateTime: string;
  ticketNo?: string;
  depTerminal: string;
  arrTerminal: string;
  traDepTerminal: string;
  traArrTerminal: string;
  baggage: (FlightBaggageInfo | null)[];
  afsBags: (FlightBaggageInfo | null)[];
  fare: {
    detail: FlightBookOrderFaresDetail;
    summary: FlightBookOrderFaresSummary;
  };
  total: number;
  promoId: string;
  disc: number;
  supplierCode: string;
  supplierClass: string;
  rt?: boolean; // combo flight flag
  // tag: FlightTag;
  traBaggage?: any;
  routeType?: string;
  freeCabinBag: number;

  /** Flight Code Share */

  mktAirline: string;
  mktAirlineName: string;
  optAirline: string;
  optAirlineName: string;
  isCodeShare: boolean;

  traMktAirline: string;
  traMktAirlineName: string;
  traOptAirline: string;
  traOptAirlineName: string;
  traIsCodeShare: boolean;
  layoverInfo: FlightLayoverInfo | null;

  rescheduleable: boolean;
  refundable: boolean;
  refundPolicy?: FlightFaresPolicy | null;
  reschedulePolicy?: FlightFaresPolicy | null;
};

export type BookCustomerInfo = {
  id: string;
  status: string;
  title: string;
  firstName: string;
  lastName: string;
  mobileCode: string;
  mobile: string;
  email: string;
  lang: string;
  country: string;
};

export type BookRefundDetail = {
  [orderId: string]: {
    currency: string;
    value: number;
    status: string;
    reason?: string;
    insertedTime?: string;
    ordType?: string;
  };
};

export type FlightBookPassengerInfo = {
  id: string;
  title: string;
  firstName: string;
  lastName: string;
  dob: string;
  gender: string;
  nationality: string;
  lang: string;
  idCard: string;
  idCardCountry: string;
  idCardCountryName?: string;
  idCardExpDate: string;
  passport: string;
  passportCountry: string;
  passportCountryName?: string;
  passportExpDate: string;
  nationalityName: string;
};

export type BookPaymentDetail = {
  id: number;
  dateTime: string;
  method: string;
  bank: string;
  optBank: string;
  name: string;
  status: string;
  currency: string;
  amount: number;
  disc: number;
  processingFee: number;
  prvCurrency: string;
  prvAmount: number;
  prvProcessingFee: number;
  token: string;
  data:
    | string
    | {
        account_name?: string;
        account_branch_name?: string;
        account_number?: string;
        bank?: string;
        reference?: string;
        redirect_link?: string;
        payment_code?: string;
        billerCode?: string;
        ref1?: string;
        qr_code?: string;
        value?: string;
        type?: string;
        qrImage?: string;
        info?: {
          confirmation_code?: string;
        };
        [key: string]: any;
      }
    | null;
  issuingBank: string;
  img: string;
  bankAccNo?: string;
  bankAccName?: string;
  afterPayInstructions?: string[];
  uniqueCode: string;
  isPaymentLink: boolean;
};

export type BookAfsGroup = {
  type: string;
  name: string;
};
export type BookAfsDetail = {
  type: string;
  trxId: string;
  insertedDateTime: string;
  updatedDateTime: string;
  status: string;
};
export type BookDetailBase = {
  dateTimeInserted: string;
  dateTimeUpdated: string;
  issuedDate: string;
  trxId: string;
  oldTrxId: string;
  status: Omit<BookStatus, 'expiryDate'>;
  currency: string;
  total: number;
  expiryDate: string;
  expiryDuration: number;
  bookExpiryDate: string;
  paymentExpiryDate: string;
  paymentExpiryDuration: number;
  paymentSuccessTime: string;
  paymentLink: { value: number };
  tzOffset: number;
  deviceType: string;
  holdable: string;
  uniqueCode: string;
  affiliate: string;
  affiliateClickId: string;
  customer: BookCustomerInfo;
  payments: BookPaymentDetail[];
  payment?: BookPaymentDetail | null;
  refund: BookRefundDetail;
  refundSummary?: {
    amount: number;
    currency: string;
    method: string;
    status: string;
    details?: { amount: number; currency: string; method: string }[] | null;
  };
  afsGroups?: BookAfsGroup[];
  afsList?: BookAfsDetail[];
  afsType?: string;
  sourceTrxId?: string;
  isExpired?: boolean;
};

export type FlightOrderBaggageInfo = {
  unit: string;
  fba: {
    type: string;
    weight: number;
    qty: number;
  } | null;
  paid: {
    type: string;
    weight: number;
    qty: number;
  } | null;
};

export type FlightOrder = Omit<FlightFare, 'id' | 'sgn' | 'pricingDifference' | 'isCombo'> & {
  id: string;
  expiresAt: number;
  type: FlightStopsTypeMultifare;
  cabin: FlightCabinType;
  stops: number;
  duration: number;
  tags: FlightTag[];
  segments: FlightSegment[];
  ticketNumbers: number[];
  pnrs: string[];
  checkInUrl: string;
  onlineCheckInMsgId: string;
  onlineCheckIn: number | null;
  baggage: {
    total: number;
    list: {
      before: FlightOrderBaggageInfo | null;
      current: FlightOrderBaggageInfo | null;
    }[];
  };
};

export type FlightBookMultiFare = {
  id: number;
  type: string;
  status: string;
  dptFlight: FlightOrder;
  rtnFlight: FlightOrder | null;
};

export type FlightBookDetail = BookDetailBase & {
  type: 'flight';
  passengers: (FlightBookPassengerInfo & {
    // added internally
    type?: 'adult' | 'child' | 'infant';
  })[];
  flight: {
    orders: FlightBookMultiFare[];
    pricing: Omit<FlightFarePrice, 'display' | 'displayBeforeDisc' | 'displayDisc' | 'totalBeforeDisc'> & {
      disc: number;
    };
    version: number;
  };
  orders: FlightBookOrderDetail[];
  hasAlphaNameNotification: boolean;
};

export type FlightBookRequestBody = {
  bookId: string;
  lang: string;
  contact: FlightBookContact & { mobileCode: string };
  passengers: FlightBookPassengerWithAddOn[];
  country: string;
  campaign: string;
  register: boolean;
  roundtripOrder: boolean;
  affiliate?: any;
  ads?: any;
  landingPage?: any;
  ip?: string;
};

export type FindBookParams = {
  type: string;
  date: string;
  phoneCode: string;
  phone: string;
  name: string;
  email: string;
  depAirport?: string;
  arrAirport: string;
};

export type HotelBookContact = BookContact & { passport?: string };

export type HotelBookGuest = {
  title: string;
  name: string;
};

export type HotelBookFormConfig = {
  expired: string;
  lang: string;
  currency: string;
  checkInDate: string;
  checkOutDate: string;
  rooms: number;
  adult: number;
  child: number;
  childAges: number[];
  hotel: Hotel & { rooms: (HotelRoom & { roomCount: number })[] };
  forms: {
    firstName: boolean;
    email: boolean;
    mobile: boolean;
    country: boolean;
    bookForSomeone: boolean;
    specialRequest: boolean;
    passport: boolean;
  };
  contacts?: UserContact[];
};

export type HotelBookOrderPhoto = {
  caption?: string;
  url: string;
};

export type HotelBookOrderPolicy = {
  type: string;
  name: string;
  content: string;
};

export type HotelBookOrderRoomCancellationInfo = HotelRoomCancellationInfo;

export type HotelBookOrderRoomCancellation = {
  term: string;
  info: HotelBookOrderRoomCancellationInfo[];
};

export type HotelBookOrderRoomSurcharge = {
  name: string;
  amount: number;
  type: 'mandatory' | 'excluded';
};

export type HotelBookOrderRoom = {
  roomId: string;
  blockId: string;
  countRoom: number;
  name: string;
  amenities: any[];
  includeBreakfast: boolean;
  includeBreakfastCount: number;
  maxOccupancy: number;
  bedType: string;
  description: string;
  roomSizeImperial: string;
  isRefundable: boolean;
  refundableUntil: string;
  searchId: string;
  photos: HotelBookOrderPhoto[];
  cancellation: {
    [lang: string]: HotelBookOrderRoomCancellation;
  };
  currency: string;
  price: number;
  basePrice: number;
  tax: number;
  surcharges: HotelBookOrderRoomSurcharge[];
  hotelCurrency: string;
  hotelPrice: number;
  hotelBasePrice: number;
  hotelTax: number;
  hotelSurcharges: any[];
  ccRequired: boolean;
  cvvRequired: boolean;
  includeFreeWifi: boolean;
  includedExtraBed: boolean;
  roomCount?: number;
};

export type HotelBookOrderDetail = {
  orderId: number;
  orderType: string;
  orderStatus: string;
  supplierId: string;
  adult: number;
  child: number;
  childAges: number[];
  hotelId: string;
  searchId: string;
  bookForSomeone: boolean;
  guestTitle: string;
  guestName: string;
  bookId: string;
  countNight: number;
  hotelName: string;
  hotelAddress: string;
  hotelContact: string;
  hotelPolicy: HotelBookOrderPolicy[];
  currency: string;
  checkInDateTime: string;
  checkOutDateTime: string;
  latitude: string;
  longitude: string;
  country: string;
  city: string;
  cityId: string;
  district: string;
  total: number;
  disc: number;
  price: number;
  tax: number;
  service: number;
  book: number;
  surcharge: number;
  specialRequest: string;
  deviceType: string;
  rooms: HotelBookOrderRoom[];
  photos: HotelBookOrderPhoto[];
  star: string;
  ccRequired: boolean;
  cvvRequired: boolean;
  isRefundable: boolean;
  refundAmount: number;
  paymentMethods: string[];
  remark: string;
};

export type HotelBookDetail = BookDetailBase & {
  type: 'hotel';
  orders: HotelBookOrderDetail[];
};

export type AfterSalesFlightBookOrderDetail = {
  id: string;
  status: string;
  expiryDateTime: string;
  currency: string;
  total: number;
  custReason: string;
  supportReason: string;
  flight: {
    [FlightBookSelectionType.DEPART]: Flight;
    [FlightBookSelectionType.RETURN]?: Flight;
  };
  psgChanges?: Array<{
    passengerId: string;
    attributes: {
      [key: string]: {
        from: string;
        to: string;
      };
    };
  }>;
  bagChanges?: {
    passengerId: string;
    [FlightBookSelectionType.DEPART]: {
      from: {
        value: number;
        size: number;
        unit: string;
      };
      to: {
        value: number;
        size: number;
        unit: string;
      };
    };
    [FlightBookSelectionType.RETURN]?: {
      from: {
        value: number;
        size: number;
        unit: string;
      };
      to: {
        value: number;
        size: number;
        unit: string;
      };
    };
  }[];
};

export type AfterSalesFlightBookDetail = BookDetailBase & {
  type: 'afs-flight'; // transformed by book service
  afsType: AfterSalesType;
  afsStatus: string;
  sourceTrxId: string;
  orders: AfterSalesFlightBookOrderDetail[];
  passengers: FlightBookPassengerInfo[];
  histories: {
    dateTime: string;
    status: string;
  }[];
};

export type PaymentLinkBookOrderDetail = {
  orderId: number;
  orderStatus: string;
  total: number;
};

export type PaymentLinkBookDetail = BookDetailBase & {
  type: 'payment_link';
  orders: PaymentLinkBookOrderDetail[];
};

export type MultiProductFlightAdditional = {
  code: string;
  traCode: string;
  depDateTime: string;
  depAirport: string;
  arrAirport: string;
  airlineName: string;
};

export type MultiProductHotelAdditional = {
  name: string;
  checkInDate: string;
};

export type MultiProductBookOrderDetail = {
  orderId: number;
  orderStatus: string;
  category: string;
  img?: string;
  title: string;
  description?: string;
  amount: number;
  qty: number;
  total: number;
  sourceTrxId: string;
  additionalDataType: string;
  additionalData: MultiProductHotelAdditional | MultiProductFlightAdditional | null;
  additionalInfo?: string;
};

export type MultiProductBookDetail = BookDetailBase & {
  type: 'multiproduct';
  sourceTrxId: string;
  orders: MultiProductBookOrderDetail[];
};
